import React, { useState } from 'react';
import { Card, Row, Col, Typography, Statistic, Button, Alert, Form, Input, Select } from 'antd';
import SearchableSelect from '../components/common/SearchableSelect';
import { ReloadOutlined } from '@ant-design/icons';
import { useDashboardData, useTestRPC } from '../hooks/useRPCDashboard';
import { useNavigate } from 'react-router-dom';
import SystemHealthCard from '../components/rpc/SystemHealthCard';
import NetworkStatusCard from '../components/rpc/NetworkStatusCard';
import NetworksTable from '../components/rpc/NetworksTable';
import RecommendationsList from '../components/rpc/RecommendationsList';
import CircuitBreakerStatusPanel from '../components/dashboard/CircuitBreakerStatusPanel';
import DepositDetectionStatusPanel from '../components/dashboard/DepositDetectionStatusPanel';
import { useTranslationWithNamespace } from '../hooks/useTranslationWithNamespace';
import { useTranslation } from 'react-i18next';

const { Title } = Typography;
const { Option } = Select;

const DashboardPage: React.FC = () => {
  const { t: commonT } = useTranslation();
  const { t: dashboardT } = useTranslationWithNamespace('dashboard');
  const { t: rpcT } = useTranslationWithNamespace('rpc');
  const navigate = useNavigate();
  const [testUrl, setTestUrl] = useState('');
  const [testNetwork, setTestNetwork] = useState('');
  const [testResult, setTestResult] = useState<any>(null);
  const [testLoading, setTestLoading] = useState(false);

  // Fetch dashboard data with auto-refresh every 60 seconds
  const {
    data: dashboardData,
    isLoading: isDashboardLoading,
    error: dashboardError,
    refetch: refetchDashboard
  } = useDashboardData(60000);

  // RPC testing mutation
  const testRPC = useTestRPC();

  // Handle manual refresh
  const handleRefresh = () => {
    refetchDashboard();
  };

  // Handle RPC test
  const handleTestRPC = async () => {
    if (!testUrl || !testNetwork) {
      setTestResult({
        success: false,
        message: 'Please provide both URL and network'
      });
      return;
    }

    setTestLoading(true);
    setTestResult(null);

    try {
      const result = await testRPC.mutateAsync({ url: testUrl, network: testNetwork });
      setTestResult({
        success: true,
        message: 'RPC endpoint is working correctly',
        data: result
      });
    } catch (error: any) {
      setTestResult({
        success: false,
        message: error.message || 'Failed to test RPC endpoint'
      });
    } finally {
      setTestLoading(false);
    }
  };

  // Prepare data for display
  const systemHealth = dashboardData?.systemHealth || {
    healthPercentage: 0,
    activeRPCs: 0,
    totalRPCs: 0
  };

  const networks = dashboardData?.networks || {};
  const recommendations = dashboardData?.recommendations || [];

  // Calculate network stats
  const networkStats = {
    total: Object.keys(networks).length,
    healthy: 0,
    warning: 0,
    unhealthy: 0
  };

  Object.entries(networks).forEach(([_, rpcs]: [string, any]) => {
    const activeCount = rpcs.filter((rpc: any) => rpc.status === 'active').length;
    const healthPercentage = rpcs.length > 0 ? (activeCount / rpcs.length) * 100 : 0;

    if (healthPercentage >= 80) {
      networkStats.healthy++;
    } else if (healthPercentage >= 50) {
      networkStats.warning++;
    } else {
      networkStats.unhealthy++;
    }
  });

  // Queue metrics data
  const queueData = dashboardData?.queueMetrics || {
    waitingJobs: 0,
    processingRate: '0',
    avgProcessingTime: '0'
  };

  // Dashboard queue data for display
  const dashboardQueueData = {
    totalWaiting: queueData.waitingJobs || 0,
    totalActive: 0,
    processingRate: queueData.processingRate || '0',
    avgProcessingTime: queueData.avgProcessingTime || '0'
  };

  return (
    <div>
      <Row gutter={[16, 16]} align="middle">
        <Col flex="auto">
          <Title level={2}>{dashboardT('title', 'Dashboard')}</Title>
        </Col>
        <Col>
          <Button
            icon={<ReloadOutlined />}
            onClick={handleRefresh}
            loading={isDashboardLoading}
          >
            {commonT('actions.refresh', 'Refresh')}
          </Button>
        </Col>
      </Row>

      {dashboardError && (
        <Alert
          message={commonT('error.title', 'Error')}
          description={commonT('error.fetchingDashboard', 'Failed to fetch dashboard data')}
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      {/* System Health Section */}
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <SystemHealthCard
            healthPercentage={systemHealth.healthPercentage}
            activeRPCs={systemHealth.activeRPCs}
            totalRPCs={systemHealth.totalRPCs}
            loading={isDashboardLoading}
          />
        </Col>

        {/* Network Status Section */}
        <Col xs={24} lg={12}>
          <NetworkStatusCard
            stats={networkStats}
            loading={isDashboardLoading}
          />
        </Col>
      </Row>

      {/* Test RPC Section */}
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24}>
          <Card title={rpcT('testRPC.title', 'Test RPC Endpoint')}>
            <Row gutter={[16, 16]}>
              <Col xs={24} md={10}>
                <Form.Item label={rpcT('testRPC.url', 'RPC URL')}>
                  <Input
                    placeholder="https://example.com/rpc"
                    value={testUrl}
                    onChange={(e) => setTestUrl(e.target.value)}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={10}>
                <Form.Item label={rpcT('testRPC.network', 'Network')}>
                  <SearchableSelect
                    placeholder={rpcT('testRPC.selectNetwork', 'Select Network')}
                    searchPlaceholder="Search networks..."
                    value={testNetwork || undefined}
                    onChange={(value) => setTestNetwork(value)}
                    style={{ width: '100%' }}
                  >
                    <Option value="ETHEREUM">Ethereum</Option>
                    <Option value="BTC">Bitcoin</Option>
                    <Option value="BSC">Binance Smart Chain</Option>
                    <Option value="POLYGON">Polygon</Option>
                    <Option value="SOLANA">Solana</Option>
                    <Option value="TON">TON</Option>
                    <Option value="TRON">Tron</Option>
                    <Option value="WAVES">Waves</Option>
                    <Option value="XRP">XRP</Option>
                  </SearchableSelect>
                </Form.Item>
              </Col>
              <Col xs={24} md={4}>
                <Form.Item label=" " colon={false}>
                  <Button
                    type="primary"
                    onClick={handleTestRPC}
                    loading={testLoading}
                    style={{ width: '100%' }}
                  >
                    {rpcT('testRPC.test', 'Test')}
                  </Button>
                </Form.Item>
              </Col>
            </Row>

            {testResult && (
              <Alert
                message={testResult.success ? rpcT('testRPC.success', 'Success') : rpcT('testRPC.error', 'Error')}
                description={testResult.message}
                type={testResult.success ? 'success' : 'error'}
                showIcon
              />
            )}
          </Card>
        </Col>
      </Row>

      {/* Networks and Recommendations Section */}
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} lg={16}>
          <Card loading={isDashboardLoading}>
            <NetworksTable
              networks={networks}
              loading={isDashboardLoading}
            />
          </Card>
        </Col>

        <Col xs={24} lg={8}>
          <Card
            title={dashboardT('recommendations.title', 'Recommendations')}
            loading={isDashboardLoading}
          >
            <RecommendationsList
              recommendations={recommendations}
              loading={isDashboardLoading}
            />
          </Card>
        </Col>
      </Row>

      {/* Queue Metrics and Circuit Breaker Section */}
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} lg={8}>
          <Card title={dashboardT('queueMetrics.title', 'Queue Metrics')} loading={isDashboardLoading}>
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={8}>
                <Statistic
                  title={dashboardT('queueMetrics.waitingJobs', 'Waiting Jobs')}
                  value={dashboardQueueData.totalWaiting}
                  valueStyle={{
                    color: dashboardQueueData.totalWaiting > 10 ? '#f5222d' : '#52c41a'
                  }}
                />
              </Col>
              <Col xs={24} sm={8}>
                <Statistic
                  title={dashboardT('queueMetrics.processingRate', 'Processing Rate')}
                  value={dashboardQueueData.processingRate}
                  suffix={dashboardT('queueMetrics.jobsPerMinute', 'jobs/min')}
                />
              </Col>
              <Col xs={24} sm={8}>
                <Statistic
                  title={dashboardT('queueMetrics.avgProcessingTime', 'Avg Processing Time')}
                  value={dashboardQueueData.avgProcessingTime}
                  suffix={dashboardT('queueMetrics.ms', 'ms')}
                />
              </Col>
            </Row>
            <div style={{ marginTop: 16 }}>
              <Button type="link" onClick={() => navigate('/queues')}>
                {dashboardT('queueMetrics.viewDetails', 'View Details')}
              </Button>
            </div>
          </Card>
        </Col>

        <Col xs={24} lg={8}>
          <CircuitBreakerStatusPanel />
        </Col>

        <Col xs={24} lg={8}>
          <DepositDetectionStatusPanel />
        </Col>
      </Row>
    </div>
  );
};

export default DashboardPage;
